import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTour } from '@reactour/tour';

// external components
import AllChats from 'eCoach/allChats/_index';
import ErrorBoundary from 'layout/components/ErrorBoundary';
import StatusTabs from 'eCoach/allChats/StatusTabs';
import { TopBar } from 'topBar/components/TopBar';

// style
import * as PageLayout from 'layout/styles/PageLayout.style';
import * as Text from 'theme/components/Text.style';
import * as AllChatsStyles from 'eCoach/_styles/AllChats.style';
import * as Buttons from 'theme/components/Buttons.style';

// icons
import { General } from 'icons/untitledui';

// hooks
import dayjs from 'shared/helpers/dayjs';
import useWindowSize from 'shared/hooks/useWindowResize';
import { useScrolledPast } from 'topBar/hooks/useScrolledPast';
import {
  FeedbackLoadingStatus,
  useFeedbackConsumer,
} from 'eCoach/hooks/useFeedbackConsumer';
import {
  useECoachSession,
  useCreateSession,
  ECoachSession as ECoachSessionType,
} from 'shared/hooks/api/ecoach';

// constants
import routesConfig from 'constants/routesConfig.json';
import breakpoints from 'theme/atoms/breakpoints';
import { TabSlug, isTabSlug, TAB_SLUG, ECoachView } from 'constants/ecoachTabs';

// local components
import ECoachSession from 'eCoach/session/ECoachSession';
import FeedbackForm from 'eCoach/FeedbackForm';
import ConversationsMovingModal from 'eCoach/ConversationsMovingModal';

interface ChatGuideData {
  chatStartEnded: boolean;
  displayPolicyPrompt: boolean;
  displayPromptHelper: boolean;
  displayUnionPrompt: boolean;
  inProgress: boolean;
  promptHelperGoal: string;
  promptHelperTheme: string;
}

const chatGuideSetup: ChatGuideData = {
  chatStartEnded: false,
  displayPolicyPrompt: false,
  displayPromptHelper: false,
  displayUnionPrompt: false,
  inProgress: false,
  promptHelperGoal: '',
  promptHelperTheme: '',
};

interface RouteParams {
  id?: string;
  tab?: string;
}

export default function ChatPage(): JSX.Element {
  const { t } = useTranslation('pageEcoach');
  const { isOpen: isTourOpen } = useTour();
  const history = useHistory();
  const location = useLocation();
  const params = useParams<RouteParams>();
  const { id, tab } = params;

  const [view, setView] = useState<ECoachView>('none');
  const [activeSession, setActiveSession] = useState<ECoachSessionType | null>(
    null
  );
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [showConversationsMovingModal, setShowConversationsMovingModal] =
    useState<boolean>(
      !localStorage.getItem('conversations_moving_modal_dismissed')
    );
  const [steps, setSteps] = useState<any[]>(activeSession?.steps || []);
  const [chatGuideData, setChatGuideData] =
    useState<ChatGuideData>(chatGuideSetup);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const isScrolledPast = useScrolledPast(titleRef, view);

  const searchParamsObj = new URLSearchParams(location.search);
  const skipResolution = searchParamsObj.get('resolution') === 'false';
  const hideForNow = true;

  const { data: sessionData, isSuccess: sessionLoaded } = useECoachSession(
    id,
    skipResolution
  );
  const {
    mutate: createSession,
    data: createdSession,
    isPending: isCreatingSession,
  } = useCreateSession();

  const windowSize = useWindowSize();
  const isDesktopView = windowSize[0] > parseInt(breakpoints.tablet, 10);
  const { feedbackStatus, fetchFeedbackStatus, tryCreate } =
    useFeedbackConsumer();

  const isLoading = sessionLoaded || isCreatingSession;

  const resetChatGuideData = (): void => setChatGuideData(chatGuideSetup);

  const getLabelFromSlug = useCallback(
    (slug: TabSlug): string => {
      const labelMap: Record<TabSlug, string> = {
        [TAB_SLUG.ALL]: t('allChats.StatusTabs.All conversations'),
        [TAB_SLUG.DRAFTS]: t('allChats.StatusTabs.Drafts'),
        [TAB_SLUG.IN_PROGRESS]: t('allChats.StatusTabs.In progress'),
        [TAB_SLUG.RESOLVED]: t('allChats.StatusTabs.Resolved'),
      };
      return labelMap[slug];
    },
    [t]
  );

  const handleChatGuidance = (updates: Partial<ChatGuideData>): void => {
    const urlSearchParams = new URLSearchParams(history.location.search);
    urlSearchParams.set('new-conversation', 'true');
    history.push({ search: urlSearchParams.toString() });
    setChatGuideData((prev) => ({ ...prev, ...updates }));
  };

  const handleNewChat = useCallback(
    (skipFeedbackFetching = false): void => {
      if (!skipFeedbackFetching && activeSession)
        fetchFeedbackStatus(activeSession.id);

      setSteps([]);
      resetChatGuideData();
    },
    [activeSession, fetchFeedbackStatus]
  );

  useEffect(() => {
    if (tab) {
      const slug = isTabSlug(tab) ? (tab as TabSlug) : TAB_SLUG.ALL;
      setView(slug);
    } else if (location.pathname === routesConfig.CONVO_PAGE) {
      // If we're on /conversation, show ECoachSession (empty view)
      setView('none');
    } else {
      // If we're on /conversations, show the default tab
      setView(TAB_SLUG.ALL);
    }
  }, [tab, location.pathname]);

  useEffect(() => {
    if (id) {
      setView('none');
      if (activeSession && activeSession.id !== id) {
        setActiveSession(null);
        setSteps([]);
      }
    }
  }, [id, activeSession]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isNewConversation = searchParams.get('new-conversation') === 'true';
    const shouldResetConvo = searchParams.get('reset') === 'true';

    if (
      shouldResetConvo ||
      (!id && !feedbackStatus?.data?.feedback_needed && !isNewConversation)
    ) {
      handleNewChat();
    }
  }, [feedbackStatus, handleNewChat, id, location]);

  useEffect(() => {
    if (sessionLoaded && sessionData) {
      setActiveSession(sessionData);
      history.replace({
        pathname: `${routesConfig.CONVO_PAGE}/${sessionData.id}`,
      });
    }
  }, [sessionLoaded, sessionData, history]);

  useEffect(() => {
    if (createdSession) {
      setActiveSession(createdSession);
      history.replace({
        pathname: `${routesConfig.CONVO_PAGE}/${createdSession.id}`,
      });
    }
  }, [createdSession, history]);

  useEffect(() => {
    if (feedbackStatus.loadingStatus !== FeedbackLoadingStatus.LOADED) return;
    if (!feedbackStatus.data.feedback_needed) setActiveSession(null);
  }, [feedbackStatus, setActiveSession]);

  // Scroll to top when switching between tabs
  useEffect(() => {
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [view]);

  useEffect(() => {
    if (isDesktopView || view === 'filters') setShowFilters(true);
    else setShowFilters(false);
  }, [isDesktopView, view]);

  const getPage = (): JSX.Element => {
    if (isTabSlug(view))
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <PageLayout.PageTitleContainer ref={titleRef}>
            <Text.H3>{getLabelFromSlug(view as TabSlug)}</Text.H3>
          </PageLayout.PageTitleContainer>
          {!hideForNow && (
            <AllChatsStyles.MigrationBanner>
              <AllChatsStyles.MigrationBannerContent>
                <General.InfoCircle
                  role="button"
                  aria-label={t('allChats.MigrationBanner.openDetails')}
                  onClick={() => setShowConversationsMovingModal(true)}
                />
                <Text.P1>
                  {t('allChats.MigrationBanner.message', {
                    date: dayjs('2026-05-08').format('LL'),
                  })}
                </Text.P1>
              </AllChatsStyles.MigrationBannerContent>
              <Buttons.SecondaryLight
                $size="small"
                onClick={() => history.push(routesConfig.EVENTS_PAGE)}
              >
                {t('allChats.MigrationBanner.goToShiftNotes')}
              </Buttons.SecondaryLight>
            </AllChatsStyles.MigrationBanner>
          )}
          <AllChats
            activeSession={activeSession?.id}
            activeView={view as TabSlug}
            resetChatGuideData={resetChatGuideData}
            setActiveSession={setActiveSession}
          />
        </div>
      );

    return (
      <ECoachSession
        activeSession={activeSession}
        loading={isLoading}
        chatGuideData={chatGuideData}
        createSession={createSession}
        handleChatGuidance={handleChatGuidance}
        steps={steps}
        setSteps={setSteps}
        onBackClick={() => setView('filters')}
        showBackButton={!showFilters}
      />
    );
  };

  const searchParams = new URLSearchParams(location.search);
  const isTourActive = searchParams.get('tour') === 'true';
  const isTourFinished = searchParams.get('reset');

  return (
    <ErrorBoundary>
      {!hideForNow && (
        <ConversationsMovingModal
          isOpen={
            isTabSlug(view) && showConversationsMovingModal && !isTourOpen
          }
          onClose={() => {
            localStorage.setItem(
              'conversations_moving_modal_dismissed',
              'true'
            );
            setShowConversationsMovingModal(false);
          }}
        />
      )}
      <PageLayout.PageWithSubNav isTourActive={isTourActive}>
        {activeSession && !isTourActive && !isTourFinished && (
          <FeedbackForm
            feedbackStatus={feedbackStatus}
            handleCreate={(input: any) => {
              tryCreate(activeSession.id, input);
              setActiveSession(null);
              handleNewChat();
            }}
          />
        )}
        {showFilters && (
          <PageLayout.ColumnSubNav>
            <StatusTabs
              activeView={view}
              setActiveView={setView}
              isDesktopView={isDesktopView}
            />
          </PageLayout.ColumnSubNav>
        )}
        {view !== 'filters' && (
          <PageLayout.ContentContainer ref={contentContainerRef}>
            {!isTabSlug(view) ? null : (
              <TopBar
                onBackClick={() => setView('filters')}
                showBackButton={!showFilters}
                backButtonAriaLabel={t('Back to filters')}
                title={getLabelFromSlug(view as TabSlug)}
                showTitle={isScrolledPast}
              />
            )}
            <PageLayout.Content
              style={{
                height: 'unset',
                gap: '10px',
              }}
            >
              {getPage()}
            </PageLayout.Content>
          </PageLayout.ContentContainer>
        )}
      </PageLayout.PageWithSubNav>
    </ErrorBoundary>
  );
}
